import React, { useState, useEffect } from 'react';
import { BASEAPIURL, token } from '../../env';
import { Container, Row, Col, Card, Button, Table, Form, Alert, Spinner } from 'react-bootstrap';
import { FaPlus, FaSyncAlt, FaTimes, FaSave, FaEdit, FaTrashAlt } from 'react-icons/fa';

const Sundry = () => {
  const [sundryItems, setSundryItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    Sundry_Name: '',
    Sundry_Type: '',
    Sundry_Percentage: 0.0,
    EC_SundryID: 0
  });

  // Actual API call function
  const fetchSundryList = async () => {
    setLoading(true);
    setError('');
    try {
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "text/plain");

      const requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow"
      };

      console.log('Fetching with token:', token); // Debug: Log the token (be careful in production)
      
      const response = await fetch(`${BASEAPIURL}/GetSundryList?Token=${token}`, requestOptions);
      
      console.log('Response status:', response.status); // Debug: Log status
      console.log('Response ok:', response.ok); // Debug: Log if ok
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response body:', errorText); // Debug: Log error body
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.text();
      console.log('Raw response:', result); // Debug: Log raw response
      
      const data = JSON.parse(result);
      console.log('Parsed data:', data); // Debug: Log parsed data
      
      // Assuming the API returns an array of sundry items
      if (Array.isArray(data)) {
        console.log('Data is array, length:', data.length); // Debug
        setSundryItems(data);
      } else if (data && typeof data === 'object') {
        // Handle different response structures
        const list = Array.isArray(data.SundryList) ? data.SundryList : [data];
        console.log('Using SundryList or fallback, length:', list.length); // Debug
        setSundryItems(list);
      } else {
        console.log('Unexpected data type, setting empty'); // Debug
        setSundryItems([]);
      }
    } catch (err) {
      console.error('Fetch error:', err); // Debug
      setError('Failed to fetch sundry list. Please try again.');
      setSundryItems([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setIsAdding(false);
    setEditingItem(null);
    setFormData({
      Sundry_Name: '',
      Sundry_Type: '',
      Sundry_Percentage: 0.0,
      EC_SundryID: 0
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real implementation, you would call the actual save API here
    // For now, we'll just reset the form
    resetForm();
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      Sundry_Name: item.Sundry_Name,
      Sundry_Type: item.Sundry_Type,
      Sundry_Percentage: item.Sundry_Percentage,
      EC_SundryID: item.EC_SundryID
    });
  };

  const handleDelete = (id) => {
    if (!window.confirm('Are you sure you want to delete this sundry item?')) {
      return;
    }
    // In a real implementation, you would call the actual delete API here
    // For now, we'll just filter the item out
    setSundryItems(prev => prev.filter(item => item.Sundry_ID !== id));
  };

  useEffect(() => {
    fetchSundryList();
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(to bottom right, #eff6ff, #e0e7ff)', padding: '1rem' }}>
      <Container>
        {/* Header */}
        <Card className="mb-4">
          <Card.Body>
            <Row className="align-items-center">
              <Col>
                <h1 className="h3 mb-2 text-dark">Sundry Management</h1>
                <p className="text-muted mb-0">Manage additional services and charges</p>
              </Col>
              <Col md="auto">
                <div className="d-flex gap-2">
                  <Button 
                    variant="outline-secondary" 
                    onClick={fetchSundryList} 
                    disabled={loading}
                  >
                    {loading ? <Spinner size="sm" className="me-1" /> : <FaSyncAlt className="me-1" />} Refresh
                  </Button>
                  <Button 
                    variant="primary" 
                    onClick={() => setIsAdding(true)}
                  >
                    <FaPlus className="me-1" /> Add Sundry
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Error Message */}
        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}

        {/* Form Section */}
        {(isAdding || editingItem) && (
          <Card className="mb-4">
            <Card.Body>
              <Row className="align-items-center mb-3">
                <Col>
                  <h2 className="h4 mb-0 text-dark">
                    {editingItem ? 'Edit Sundry Item' : 'Add New Sundry Item'}
                  </h2>
                </Col>
                <Col md="auto">
                  <Button 
                    variant="link" 
                    onClick={resetForm} 
                    className="p-0 text-decoration-none text-dark"
                  >
                    <FaTimes />
                  </Button>
                </Col>
              </Row>
              
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Sundry Name <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="Sundry_Name"
                        value={formData.Sundry_Name}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter sundry name"
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Sundry Type <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="Sundry_Type"
                        value={formData.Sundry_Type}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter sundry type"
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Percentage (%)</Form.Label>
                      <Form.Control
                        type="number"
                        name="Sundry_Percentage"
                        value={formData.Sundry_Percentage}
                        onChange={handleInputChange}
                        step="0.1"
                        min="0"
                        placeholder="0.0"
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>EC Sundry ID</Form.Label>
                      <Form.Control
                        type="number"
                        name="EC_SundryID"
                        value={formData.EC_SundryID}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="Enter EC Sundry ID"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row className="pt-2">
                  <Col md="auto">
                    <Button 
                      type="submit" 
                      variant="success" 
                      disabled={loading} 
                      className="me-2"
                    >
                      <FaSave className="me-1" /> Save
                    </Button>
                  </Col>
                  <Col md="auto">
                    <Button 
                      type="button" 
                      variant="secondary" 
                      onClick={resetForm}
                    >
                      Cancel
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        )}

        {/* Sundry List */}
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h3 className="mb-0">Sundry Items ({sundryItems.length})</h3>
          </Card.Header>
          
          <Card.Body>
            {loading && !isAdding && !editingItem ? (
              <div className="text-center py-5">
                <Spinner animation="border" className="mb-2" />
                <p className="text-muted mb-0">Loading sundry items...</p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover>
                  <thead className="table-light">
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Percentage (%)</th>
                      <th>EC ID</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sundryItems.map((item) => (
                      <tr key={item.Sundry_ID}>
                        <td className="fw-medium">{item.Sundry_ID}</td>
                        <td className="fw-semibold text-dark">{item.Sundry_Name}</td>
                        <td>{item.Sundry_Type}</td>
                        <td>{item.Sundry_Percentage}%</td>
                        <td>{item.EC_SundryID}</td>
                        <td className="text-end">
                          <div className="d-flex justify-content-end gap-1">
                            <Button
                              variant="link"
                              size="sm"
                              onClick={() => handleEdit(item)}
                              className="p-0 text-primary"
                              title="Edit"
                            >
                              <FaEdit />
                            </Button>
                            <Button
                              variant="link"
                              size="sm"
                              onClick={() => handleDelete(item.Sundry_ID)}
                              className="p-0 text-danger"
                              title="Delete"
                            >
                              <FaTrashAlt />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default Sundry;